"use client";
import { useNoteContext } from "@/contexts/NoteContext";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useAuth } from "@/hooks/use-auth";
import { usePathname } from "next/navigation";
import { extractNoteId } from "@/utils/noteUrl";
import { useEffect, useState } from "react";
import ChatInterface from "./ui/chat-interface";
import { X, PenSquare, MoreHorizontal } from "lucide-react";
import Image from "next/image";

export default function ChatWidget() {
  const { isPremiumUser } = useNoteContext();
  const [open, setOpen] = useState(false);
  const { selectedNoteId, activeTitle, notes, editorTitle } = useNoteContext();
  const { currentWorkspace } = useWorkspaceContext();
  const { user } = useAuth();
  const pathname = usePathname() || "";

  const [currentNoteId, setCurrentNoteId] = useState<string | null>(selectedNoteId);
  const [currentNoteTitle, setCurrentNoteTitle] = useState<string | undefined>(editorTitle);
  const [isThinking, setIsThinking] = useState(false);
  const [newChatVersion, setNewChatVersion] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const noteIdFromPath = extractNoteId(pathname.match(/^\/notes\/([^\/\?]+)/)?.[1]) || null;
    const noteId = selectedNoteId || noteIdFromPath;
    setCurrentNoteId(noteId);

    if (noteId && !editorTitle && notes) {
      const note = notes.find((n) => n.id === noteId);
      if (note) setCurrentNoteTitle(note.title);
    } else {
      setCurrentNoteTitle(editorTitle);
    }
  }, [pathname, selectedNoteId, notes, editorTitle]);

  useEffect(() => {
    const checkNavigation = () => {
      const pendingNav = window.sessionStorage.getItem("pendingNavigation");
      const navTimestamp = window.sessionStorage.getItem("navigationTimestamp");
      if (pendingNav && navTimestamp) {
        const timestamp = Number.parseInt(navTimestamp, 10);
        const isRecent = Date.now() - timestamp < 2000;
        if (isRecent) {
          if (window.location.pathname === pendingNav) {
            window.sessionStorage.removeItem("pendingNavigation");
            window.sessionStorage.removeItem("navigationTimestamp");
            return;
          }
          window.location.href = pendingNav;
          window.sessionStorage.removeItem("pendingNavigation");
          window.sessionStorage.removeItem("navigationTimestamp");
        } else {
          window.sessionStorage.removeItem("pendingNavigation");
          window.sessionStorage.removeItem("navigationTimestamp");
        }
      }
    };
    checkNavigation();
    const interval = setInterval(checkNavigation, 1000);
    setTimeout(() => clearInterval(interval), 3000);
    return () => clearInterval(interval);
  }, [pathname]);

  const handleThinkingChange = (thinking: boolean) => {
    setIsThinking(thinking);
  };

  const handleNewChat = () => {
    setNewChatVersion((prev) => prev + 1);
    setShowHistory(false);
  };

  const isNotesRoute = pathname.startsWith("/notes") && pathname !== "/notes/home";

  if (!isPremiumUser || !isNotesRoute) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          // bottom/right pad for the home indicator + side safe-area on
          // notched devices. max(1.5rem, ...) keeps the existing 24px offset
          // on desktop and non-notched mobile.
          className="fixed z-[1000] transition-transform duration-200 hover:scale-105 active:scale-95 bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-[max(1.5rem,env(safe-area-inset-right))]"
          aria-label="Open chat"
          title="Open chat"
        >
          <Image src="/Chatboat Logo.svg" alt="Chat" width={54} height={54} className="drop-shadow-[0_4px_12px_rgba(0,0,0,0.25)]" />
        </button>
      )}

      {open && (
        <div
          // Same safe-area-aware positioning as the closed-state button so the
          // open panel doesn't sit under the iOS home indicator.
          className="fixed z-[1001] w-[380px] max-w-[95vw] flex flex-col rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-background dark:bg-[#2c2c2c] overflow-hidden bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-[max(1.5rem,env(safe-area-inset-right))]"
          style={{ height: 480 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-3 bg-background dark:bg-[#2c2c2c]">
            {/* Left: menu + title */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="p-1.5 rounded hover-component  transition-colors text-gray-500 dark:text-gray-400"
                aria-label="Chat history"
                title="Chat history"
              >
                <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                  <path fill="currentColor" d="M15 12.5V14H1v-1.5zm0-5.25v1.5H1v-1.5zM15 2v1.5H1V2z" />
                </svg>
              </button>

              <div className="flex items-center gap-2">
                <Image
                  src="/Chatboat Logo.svg"
                  alt="Assistant"
                  width={26}
                  height={26}
                  className={isThinking ? "animate-pulse" : ""}
                />
                <span className="text-sm font-semibold text-gray-900 dark:text-white">Closot AI</span>
              </div>
            </div>

            {/* Right: action buttons */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleNewChat}
                className="p-1.5 rounded hover-component  transition-colors text-gray-500 dark:text-gray-400"
                aria-label="New chat"
                title="New chat"
              >
                <PenSquare size={15} />
              </button>
              <button
                type="button"
                className="p-1.5 rounded hover-component  transition-colors text-gray-500 dark:text-gray-400"
                aria-label="More options"
              >
                <MoreHorizontal size={15} />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded hover-component  transition-colors text-gray-500 dark:text-gray-400"
                aria-label="Close"
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden">
            <ChatInterface
              currentNoteId={currentNoteId}
              currentNoteTitle={currentNoteTitle}
              userEmail={user?.email}
              userName={user?.name}
              activeWorkspaceId={currentWorkspace?._id}
              onThinkingChange={handleThinkingChange}
              newChatVersion={newChatVersion}
              showHistory={showHistory}
              onHistoryClose={() => setShowHistory(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
